import React from 'react';
import {PathUtility} from 'src/shared/constants/PathBuilder';

import type {InstanceResponse} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {ContentWrapper} from 'src/app/layout/ContentWrapper';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetInstanceListV2Query} from 'src/redux/queries/instance';
import useSetDocumentTitle from 'src/shared/hooks/useSetDocumentTitle';
import {instanceDocumentTitle} from '../instanceHelpers';
import {useInstanceColumns} from './hooks/useInstanceColumns';

import {SidebarLinkInstances} from 'src/app/layout/Sidebar/SidebarLinksConfig';
import {routeRegistry} from 'src/pages/RouteRegistry';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export const InstanceListPage: React.FC = () => {
  useSetDocumentTitle(instanceDocumentTitle);
  const navigate = useAppNavigate();

  const columns = useInstanceColumns();

  const tableState = useTableState({
    identifier: 'instance-list-page',
    columns,
    defaultColumnVisibility: {},
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: [{id: 'name', desc: false}],
    defaultShowColumnFilters: true,
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetInstanceListV2Query(
      {
        start,
        length,
        filters,
        filterFns,
        sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const handleAddNew = () => navigate(PathUtility.InstancesCreate());

  const handleNavigateToInstance = (row: InstanceResponse) =>
    navigate(PathUtility.InstancesView(String(row.id), 'overview'));

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns,
      data: rows,
      getRowId: row => String(row.id),

      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() => handleNavigateToInstance(row.original)}
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            <TableToolbar.Action variant="create" onClick={handleAddNew}>
              New Instance
            </TableToolbar.Action>
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return (
    <ContentWrapper>
      <PageHeader>
        <PageHeader.Title
          icon={SidebarLinkInstances.icon}
          subtitle="Manage workspaces">
          Instances
        </PageHeader.Title>
      </PageHeader>
      {table}
    </ContentWrapper>
  );
};

export default InstanceListPage;

routeRegistry.registerPageRoute({
  backNavigation: 'page',
  path: PathUtility.Instances(),
  element: <InstanceListPage />,
  isPrivate: true,
  requireSuperAdmin: true,
});
